/* eslint-disable jsx-a11y/anchor-is-valid */
import { Input, Radio, Select, Tag } from 'antd';
import {
  API_DEV_URL,
  API_URL,
  BLOCK_TYPES,
  CYCLE_STATUS,
  LLM_REFERRERS,
  MIGRATION_REDIRECT_ENABLED,
  MODULES_ITEMS,
  NEW_APP_NAME,
  NEW_APP_URL,
  QUESTIONS_TYPES,
  REVIEW_STATUS,
  REVIEW_VISIBILITY,
  ROLE,
} from './constants';
// import { Typography } from 'antd';
import { useEffect, useState } from 'react';
import AnimatedDot from 'components/animatedDot';
import {
  CheckCircleFilled,
  FieldTimeOutlined,
  FormOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';

export const getSessionId = () => localStorage.getItem('sessionId');

export const isConnected = () => {
  const item = getSessionId();
  return item && item != null && item !== 'undefined';
};

// Build the hand-off URL for the new BuddiesHR dashboard webapp, carrying the
// existing session so the user stays logged in after the migration.
export const getNewAppAuthURL = (sessionId) =>
  `${NEW_APP_URL}/#/auth/${encodeURIComponent(sessionId)}?app=${NEW_APP_NAME}`;

// Hand a logged-in user off to the new webapp. Returns true if a redirect was
// triggered (so callers can short-circuit and avoid rendering the old app).
export const redirectToNewAppIfMigrated = () => {
  if (!MIGRATION_REDIRECT_ENABLED) return false;
  // Keep local development on the old app so devs aren't kicked to production.
  if (isLocalhost()) return false;
  if (!isConnected()) return false;
  window.location.href = getNewAppAuthURL(getSessionId());
  return true;
};
export const scrollTo = (selector) =>
  document.querySelector(selector).scrollIntoView({
    behavior: 'smooth',
  });
export const getCountLabel = (labelValue) =>
  Math.abs(Number(labelValue)) >= 1.0e9
    ? `${(Math.abs(Number(labelValue)) / 1.0e9).toFixed(1)}B`
    : Math.abs(Number(labelValue)) >= 1.0e6
    ? `${(Math.abs(Number(labelValue)) / 1.0e6).toFixed(1)}M`
    : Math.abs(Number(labelValue)) >= 1.0e3
    ? `${(Math.abs(Number(labelValue)) / 1.0e3).toFixed(1)}K`
    : Number.isInteger(labelValue)
    ? labelValue
    : Math.abs(Number(labelValue)).toFixed(1);

export const getApiURL = () => {
  const host = window.location.host.split('.')[0];
  if (host === 'app') {
    return API_URL;
  } else return API_DEV_URL;
};

export const isNumber = (nb) => typeof nb === 'number';

export const numberWithComma = (value) => value.toLocaleString('en-US');

export const isLocalhost = () =>
  window && window.location && window.location.hostname === 'localhost';

export const hostnameToURL = (hostname) => {
  // the inital value of the URL object can be anything
  const url = new URL('https://example.com');
  url.hostname = hostname;
  return url.href;
};

export function useWindowSize() {
  // Initialize state with undefined width/height so server and client renders match
  // Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
  const [windowSize, setWindowSize] = useState({
    width: undefined,
    height: undefined,
  });
  useEffect(() => {
    // Handler to call on window resize
    function handleResize() {
      // Set window width/height to state
      setWindowSize({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    }
    // Add event listener
    window.addEventListener('resize', handleResize);
    // Call handler right away so state gets updated with initial window size
    handleResize();
    // Remove event listener on cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []); // Empty array ensures that effect is only run on mount
  return windowSize;
}

export const getCreatedDateFromMongoId = (objectId) => {
  return new Date(parseInt(objectId.substring(0, 8), 16) * 1000);
};

export const getTagFromStatus = (cycle) => {
  if(cycle.isArchived) {
    return <></>
  }
  switch (cycle.status) {
    default:
    case CYCLE_STATUS.DRAFT:
      return (
        <Tag color="blue">
          <FormOutlined /> Draft
        </Tag>
      );
    case CYCLE_STATUS.SCHEDULED:
      return (
        <Tag color="lime">
          <FieldTimeOutlined /> Scheduled
          {cycle.scheduledAt
            ? ` for ${dayjs(cycle.scheduledAt).format('ddd Do MMM YYYY HH:mm')}`
            : ''}
        </Tag>
      );
    case CYCLE_STATUS.LIVE:
      if (cycle.stats.reviewsCount === cycle.stats.reviewsTotal) {
        return (
          <Tag color="green">
            <CheckCircleFilled />
            &nbsp;&nbsp;All reviews collected 🎉
          </Tag>
        );
      } else {
        return dayjs(cycle.deadline) < dayjs() ? (
          <Tag>Done</Tag>
        ) : (
          <Tag
            color="green"
            style={{
              display: 'flex',
              alignItems: 'center',
              width: 'fit-content',
            }}
          >
            <AnimatedDot />
            &nbsp;&nbsp;Collecting responses
          </Tag>
        );
      }
  }
};

export const getBase64 = (file, func) => {
  var filereader = new FileReader();
  filereader.readAsDataURL(file);
  filereader.onload = function (evt) {
    var base64 = evt.target.result;
    return func(base64);
  };
};

export const normFile = (e) => {
  if (Array.isArray(e)) {
    return e;
  }
  return e?.fileList;
};

export const getSLackURL = (param = '') =>
  `${getApiURL()}/web-app/admin/workspaces/slack/add?sessionid=${getSessionId()}${
    param && param.length > 0 ? `&param=${param}` : ''
  }`;

export const getQuestionInputFromType = (
  block,
  onChange,
  additionalProps = {},
  isReadOnly = false,
  additionalStyles = {}
) => {
  switch (block.params.type) {
    default:
    case QUESTIONS_TYPES.OPEN_ENDED.id:
      return (
        <Input.TextArea
          rows={6}
          autoSize={true}
          placeholder={
            isReadOnly ? 'No answer provided' : 'Type your answer here'
          }
          onChange={(e) => onChange(block._id, e.target.value)}
          style={{ paddingBottom: 60, ...additionalStyles }}
          {...additionalProps}
        />
      );
    case QUESTIONS_TYPES.MULTIPLE_CHOICE.id:
      return (
        <Select
          options={block.params.choices.map((choice) => ({
            label: choice,
            value: choice,
          }))}
          placeholder={'Choose the option that suits best'}
          onChange={(value) => onChange(block._id, value)}
          style={{ ...additionalStyles }}
          {...additionalProps}
        />
      );
    case QUESTIONS_TYPES.RATING.id:
      return (
        <>
          <Radio.Group
            style={{ marginBottom: 42, ...additionalStyles }}
            block
            options={block.params.choices.map((choice, index) => ({
              label: (
                <>
                  <div style={{ whiteSpace: 'nowrap' }}>{`${index + 1}`}</div>
                  <div
                    style={{
                      color: 'grey',
                      fontSize: 11,
                      lineHeight: '14px',
                      marginTop: 4,
                    }}
                  >
                    {choice}
                  </div>
                </>
              ),
              value: choice,
            }))}
            onChange={(value) => onChange(block._id, value.target.value)}
            optionType="button"
            buttonStyle="solid"
            {...additionalProps}
          />
        </>
      );
  }
};

export const getTagFromReviewStatus = (status) => {
  switch (status) {
    default:
    case REVIEW_STATUS.TO_DO:
      return <Tag color="purple">To do</Tag>;
    case REVIEW_STATUS.IN_PROGRESS:
      return <Tag color="blue">In progress</Tag>;
    case REVIEW_STATUS.DONE:
      return <Tag color="green">Done</Tag>;
    case REVIEW_STATUS.READY:
      return <Tag color="green">Ready</Tag>;
  }
};

export const isAdmin = (profile) => profile?.role === ROLE.ADMIN;

export const isFreeTrialExpired = (profile) => {
  return (
    profile?.workspace?.freeTrialStartDate &&
    !profile?.workspace?.buddiesSubscription &&
    profile?.workspace?.freeTrialEndDate &&
    dayjs(profile.workspace.freeTrialEndDate).isBefore(dayjs())
  );
};

export const needUpgrade = (profile) => {
  return (
    profile?.profile &&
    !profile.profile?.workspace?.buddiesSubscription &&
    isFreeTrialExpired(profile.profile)
  );
};

export const injectQuestionNumbersToModule = (module) => {
  let questionNumber = 1;
  return {
    ...module,
    blocks: module.blocks.map((block, index) => ({
      ...block,
      questionNumber:
        block.type === BLOCK_TYPES.QUESTION.id ? questionNumber++ : undefined,
    })),
  };
};

export const getMessageVisibilityFromType = (module, reviewVisibility) => {
  switch (module.type) {
    default:
    case MODULES_ITEMS.SELF.type:
      return "Once submitted, it'll be visible to HRs and your manager.";
    case MODULES_ITEMS.DOWNWARD.type:
      return reviewVisibility === REVIEW_VISIBILITY.MANAGER_RELEASE
        ? "Once submitted, it'll be visible to HRs and the manager of the person being reviewed."
        : "Once submitted, it'll be visible to HRs and the person being reviewed.";
    case MODULES_ITEMS.PEER.type:
      return reviewVisibility === REVIEW_VISIBILITY.MANAGER_RELEASE
        ? "Once submitted, it'll be visible to HRs and the manager of the person being reviewed."
        : "Once submitted, it'll be visible to HRs and the person being reviewed (+its manager).";
    case MODULES_ITEMS.UPWARD.type:
      return reviewVisibility === REVIEW_VISIBILITY.MANAGER_RELEASE
        ? "Once submitted, it'll be visible to HRs and the manager of the person being reviewed."
        : "Once submitted, it'll be visible to HRs and the person being reviewed (+its manager).";
  }
};

export const isFromLLMReferrer = () => {
  if (!document.referrer) return false
  return LLM_REFERRERS.some(domain =>
    document.referrer.includes(domain)
  )
}